/**
 * BlogPost Page
 */

import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Clock, Calendar, ArrowRight, Share2, Check, Link2 } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

// Social share icons as inline SVGs for reliability
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
  </svg>
);

const ShareButtons = ({ post }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post.title;
  const shareText = post.excerpt;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
  };

  const shareToX = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch {
        // User cancelled or error
      }
    }
  };

  return (
    <div className="flex items-center gap-3">
      <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">Share:</span>
      
      {/* Facebook */}
      <button 
        onClick={shareToFacebook}
        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-colors text-gray-600"
        title="Share on Facebook"
        data-testid="share-facebook"
      >
        <FacebookIcon />
      </button>
      
      {/* LinkedIn */}
      <button 
        onClick={shareToLinkedIn}
        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#0A66C2] hover:text-white transition-colors text-gray-600"
        title="Share on LinkedIn"
        data-testid="share-linkedin"
      >
        <LinkedInIcon />
      </button>
      
      {/* X (Twitter) */}
      <button 
        onClick={shareToX}
        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-black hover:text-white transition-colors text-gray-600"
        title="Share on X"
        data-testid="share-x"
      >
        <XIcon />
      </button>
      
      {/* Copy Link */}
      <button 
        onClick={handleCopyLink}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          copied ? 'bg-[#48d480] text-white' : 'bg-gray-100 hover:bg-[#48d480] hover:text-white text-gray-600'
        }`}
        title={copied ? "Link copied!" : "Copy link"}
        data-testid="share-copy"
      >
        {copied ? <Check size={16} /> : <Link2 size={16} />}
      </button>
      
      {/* Native Share (mobile) */}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button 
          onClick={handleNativeShare}
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#48d480] hover:text-white transition-colors text-gray-600"
          title="More share options"
          data-testid="share-native"
        >
          <Share2 size={16} />
        </button>
      )}
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Navbar />

      {/* Hero */}
      <section className="relative h-96 overflow-hidden pt-24">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2d52] via-[#0f2d52]/50 to-transparent" />
        <div className="container relative z-10 h-full flex flex-col justify-end pb-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          <span className="inline-block bg-[#48d480] text-[#0f2d52] text-xs font-bold px-3 py-1 rounded-full mb-4 w-fit">
            {post.category}
          </span>
          <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-5xl font-black text-white max-w-3xl leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 mt-6 text-white/60 text-sm">
            <span className="flex items-center gap-2"><Calendar size={14} /> {post.date}</span>
            <span className="flex items-center gap-2"><Clock size={14} /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="prose prose-lg max-w-none">
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 leading-relaxed text-lg mb-6">
                  {post.excerpt}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 leading-relaxed">
                  {post.content || "Full article content would go here. This is a placeholder for the complete blog post content that would provide detailed information about the topic."}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 leading-relaxed mt-4">
                  At Legacy Triangle, we're committed to helping businesses like yours secure the funding they need to grow and thrive. Whether you're looking for an SBA loan, equipment financing, a line of credit, or credit card stacking, our team of experts is here to guide you through every step of the process.
                </p>
                <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl font-bold text-[#0f2d52] mt-8 mb-4">Key Takeaways</h2>
                <ul style={{ fontFamily: "'Inter', sans-serif" }} className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Understanding your funding options is crucial for business success</li>
                  <li>Proper preparation can significantly improve your approval chances</li>
                  <li>Working with experienced funding specialists saves time and money</li>
                  <li>Building business credit opens doors to better funding terms</li>
                </ul>
                <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl font-bold text-[#0f2d52] mt-8 mb-4">Next Steps</h2>
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 leading-relaxed">
                  Ready to take the next step? Contact our team for a free consultation. We'll review your business situation and match you with the best funding options available.
                </p>
              </article>

              {/* Share */}
              <div className="flex items-center gap-4 mt-12 pt-8 border-t border-gray-200">
                <ShareButtons post={post} />
              </div>

              {/* CTA Box */}
              <div className="mt-12 bg-[#0f2d52] rounded-2xl p-8 text-center">
                <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl font-black text-white mb-3">
                  Ready to Get Funded?
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-6">
                  Apply now and get matched with the perfect funding solution for your business.
                </p>
                <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer">
                  <button className="btn-primary flex items-center gap-2 mx-auto">
                    Start Your Application <ArrowRight size={16} />
                  </button>
                </a>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-8">
                {/* Related Posts */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-lg mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((related) => (
                      <Link key={related.slug} to={`/blog/${related.slug}`} className="block group">
                        <div className="flex gap-3">
                          <img src={related.image} alt={related.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                          <div>
                            <h4 style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#0f2d52] font-semibold text-sm group-hover:text-[#48d480] transition-colors line-clamp-2">
                              {related.title}
                            </h4>
                            <span className="text-gray-400 text-xs">{related.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-[#48d480]/10 rounded-xl p-6 border border-[#48d480]/20">
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-lg mb-4">Quick Links</h3>
                  <div className="space-y-2">
                    <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer" className="block text-[#0f2d52] font-medium text-sm hover:text-[#48d480] transition-colors">
                      Apply for Funding →
                    </a>
                    {[
                      { label: "Funding Options", href: "/funding-options" },
                      { label: "Contact Us", href: "/contact" },
                    ].map((link) => (
                      <Link key={link.href} to={link.href} className="block text-[#0f2d52] font-medium text-sm hover:text-[#48d480] transition-colors">
                        {link.label} →
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
